import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

// Shared building blocks for pages on design system v2.

export function Container({ className, children, ...props }) {
  return (
    <div
      className={cn(
        'mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control border font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-canvas'

const BUTTON_VARIANTS = {
  primary:
    'border-primary bg-primary text-primary-foreground hover:bg-primary/90',
  secondary: 'border-ink bg-surface text-ink hover:bg-ink/5',
}

const BUTTON_SIZES = {
  sm: 'h-9 px-3.5 text-sm',
  md: 'h-11 px-5 text-[15px]',
}

export function buttonClasses({
  variant = 'primary',
  size = 'md',
  className,
} = {}) {
  return cn(
    BUTTON_BASE,
    BUTTON_VARIANTS[variant],
    BUTTON_SIZES[size],
    className
  )
}

export function ButtonLink({
  href,
  variant,
  size,
  arrow = false,
  className,
  children,
  ...props
}) {
  return (
    <Link
      href={href}
      className={buttonClasses({
        variant,
        size,
        className: cn('group', className),
      })}
      {...props}
    >
      {children}
      {arrow && (
        <ArrowRight
          aria-hidden
          className="-mr-0.5 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
        />
      )}
    </Link>
  )
}

// Technical eyebrow: short mono label above a heading. Replaces uppercase labels.
export function Eyebrow({ className, children }) {
  return (
    <p
      className={cn(
        'font-mono text-[13px] font-medium leading-5 text-signal',
        className
      )}
    >
      {children}
    </p>
  )
}

export function SectionHeading({ id, eyebrow, title, children, className }) {
  return (
    <div className={cn('reveal flex max-w-[44rem] flex-col gap-3', className)}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2
        id={id}
        className="text-balance text-[30px] font-[450] leading-[1.12] text-ink [font-stretch:110%] sm:text-[38px] lg:text-[44px]"
      >
        {title}
      </h2>
      {children && (
        <div className="text-[17px] leading-relaxed text-graphite">
          {children}
        </div>
      )}
    </div>
  )
}

// Marks product illustrations that use invented example data.
export function SampleTag({ className }) {
  return (
    <span
      className={cn(
        'rounded-[3px] border border-rule-strong bg-canvas px-1.5 py-0.5 font-mono text-[11px] leading-4 text-graphite',
        className
      )}
    >
      Beispieldaten
    </span>
  )
}
